<template>
  <div v-if="properties.length > 0">
    <div class="d-flex flex-wrap no-gutters" v-if="expandProperties">
      <template v-for="(propertyType) in properties">
        <div class="mb-3 col-xl-4 col-lg-6 pe-3" v-for="(prop) in propertyType.children" :key="prop.id">
          <div class="row no-gutters">
            <div class="col-auto align-self-center pe-1">
              <font-awesome-icon fixed-width :icon="['fas', prop.isVisible ? 'chart-bar' : 'cogs']" />
            </div>
            <div class="col">
              <treeselect :placeholder="prop.label"
                          ref="propertiesTreeselect"
                          :instance-id="prop.id"
                          v-model="internalValueExpanded[prop.id]"
                          :auto-load-root-options="false"
                          :options="prop.children"
                          :disabled="disabled"
                          :disableFuzzyMatching="true"
                          multiple
                          :limit="4"
                          :limit-text="limitText"
                          :append-to-body="false"
                          showCountOf="LEAF_DESCENDANTS" />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div v-else-if="isBusy" class="mb-3">
    <b-spinner small variant="primary"></b-spinner>
  </div>
  <div v-else class="mb-3">
    {{ $t('Webportal.NoDataFound')}}
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import EventBus from '@/services/EventBus.js';
import SurveysService from "@/services/SurveysService";

export default {
  name: "CustomPropertiesInput",

  mixins: [ AppConfig ],

  props: {
    modelValue: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    clientId: { type: Number, default: null },
    onlyVisible: { type: Boolean, default: true },
    expandProperties: { type: Boolean, default: false },
  },

  data() {
    return {
      internalValue: this.modelValue,
      internalValueExpanded: {},

      propertiesCancelToken: null,
      properties: [],

      isBusy: false,
    };
  },

  watch: {
    modelValue: {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) {
          this.internalValue = newVal;
        }
      },
      deep: true
    },

    internalValue(newVal, oldVal) {
      if (newVal != oldVal)
      {
        this.internalValueExpanded = _.groupBy(newVal, pv => pv.split(/\^/s)[0]);
        this.$emit("input", newVal);
      }
    },

    internalValueExpanded: {
      handler(newVal, oldVal) {
        // remove undefined entries
        Object.keys(newVal).forEach(key => _.isEmpty(this.internalValue[key]) && delete this.internalValue[key])

        this.$emit("input", Object.values(newVal ?? {}).flat());
      },
      deep: true
    },

    clientId() {
      this.internalValue = [];
      this.properties = [];

      this.getSurveyProperties();
    },
  },

  mounted() {
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeUnmount() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyProperties();
    },

    getSurveyProperties() {
      if (this.clientId) {
        this.isBusy = true;

        // Cancel previous request
        if (this.propertiesCancelToken) {
          this.propertiesCancelToken.cancel();
        }

        this.propertiesCancelToken = axios.CancelToken.source();

        SurveysService.getSurveyCustomProperties({ clientIds: [ this.clientId ] }, true, false, this.onlyVisible, true, this.propertiesCancelToken)
          .then((response) => {
            if (response.data && response.data.length > 0) {
              var propertyTypeMapping = {
                'true': { id: 'T1', label: this.$t('Webportal.Properties.Visible'), isVisible: true },
                'false': { id: 'T2', label: this.$t('Webportal.Properties.Hidden'), isVisible: false }
              };

              this.properties = _(response.data[0].children)
                .groupBy(p => p.IsVisible ?? true) // IsVisible is null when parameter @OnlyVisible is set to true
                .map((items, key) => ({
                  id: propertyTypeMapping[key].id,
                  label: propertyTypeMapping[key].label,
                  isVisible: propertyTypeMapping[key].isVisible,
                  children: items.map(p => ({
                    id: p.id,
                    label: p.label,
                    isVisible: propertyTypeMapping[key].isVisible,
                    children: p.children.map(pv => ({
                      id: pv.id,
                      label: pv.label,
                      property: p
                    }))
                  }))
                }))
                .orderBy(pt => pt.isVisible, 'desc')
                .value();
            }
          })
          .catch(error => {
            if (!axios.isCancel(error)) {
              console.error("Error loading survey properties: ", error);
            }
          })
          .finally(_ => {
            this.isBusy = false;
          });
      }
    },
  },
};
</script>
